/**
 * Face state store — derives the base Muse face variant from voice session events.
 *
 * The base variant is sent to voiceorb.html which combines it with real-time
 * audio levels to compute the displayed face each frame:
 *   - inactive (05) — no voice session
 *   - idle (01) — voice session active, waiting for input
 *   - agentThinking (07) — user finished speaking, waiting for response
 *
 * Listening (04) and talking (02) faces are computed in the HTML from
 * mic/audio output levels for instant response without RN bridge latency.
 */

import { create } from "zustand";

type FaceState = "inactive" | "idle" | "agentThinking";
type OrbMode = "active" | "inactive" | "animated";

const VARIANT_MAP: Record<FaceState, string> = {
  agentThinking: "07",
  idle: "01",
  inactive: "05",
};

interface FaceStoreState {
  voiceActive: boolean;
  agentThinking: boolean;
  variant: string;
  orbMode: OrbMode;
  ftueVariant: string | null;

  setVoiceActive: (v: boolean) => void;
  setAgentThinking: (v: boolean) => void;
  setOrbMode: (m: OrbMode) => void;
  setFTUEVariant: (v: string | null) => void;
  resetAll: () => void;
}

function resolveVariant(s: { voiceActive: boolean; agentThinking: boolean; ftueVariant: string | null }): string {
  if (s.ftueVariant) return s.ftueVariant;
  if (!s.voiceActive) return VARIANT_MAP.inactive;
  if (s.agentThinking) return VARIANT_MAP.agentThinking;
  return VARIANT_MAP.idle;
}

export const useFaceStore = create<FaceStoreState>((set) => ({
  voiceActive: false,
  agentThinking: false,
  variant: "05",
  orbMode: "inactive" as OrbMode,
  ftueVariant: null,

  setVoiceActive: (v) => set((s) => {
    const next = { ...s, voiceActive: v };
    if (!v) return { voiceActive: false, agentThinking: false, variant: resolveVariant(next) };
    return { voiceActive: v, variant: resolveVariant(next) };
  }),
  setAgentThinking: (v) => set((s) => {
    const next = { ...s, agentThinking: v };
    return { agentThinking: v, variant: resolveVariant(next) };
  }),
  setFTUEVariant: (v) => set((s) => {
    const next = { ...s, ftueVariant: v };
    return { ftueVariant: v, variant: resolveVariant(next) };
  }),
  setOrbMode: (m) => set({ orbMode: m }),
  resetAll: () => set({ voiceActive: false, agentThinking: false, ftueVariant: null, variant: "05", orbMode: "inactive" }),
}));
